import { useCallback, useRef } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';
import type { RunRefusal } from '@/api/runs';
import type { ConvMessage } from '../../types';
import type { SkipOutcome } from './useRunActions';

/**
 * Retry, Create another answer and Edit wait, like Send, until the Q&A has
 * loaded with its mode applied and its latest load succeeded: an answer
 * without a recorded mode is resubmitted in the mode on screen, and an
 * edited comparison takes its roles from the totals. Until then they say
 * why: still checking, or the Q&A could not be loaded (with the Retry the
 * question area offers). The callbacks are stable, so the answers do not
 * re-render while the user types.
 */
export const useResubmissionGuard = ({
  ready,
  failed,
  messageApi,
  retry,
  alternative,
  skipClarification,
  resubmitEdited,
}: {
  /** Loaded for this selection, with its mode applied. */
  ready: boolean;
  /** The latest load of the Q&A failed. */
  failed: boolean;
  messageApi: MessageInstance;
  retry: (message: ConvMessage) => Promise<RunRefusal | null>;
  alternative: (message: ConvMessage) => Promise<RunRefusal | null>;
  skipClarification: (message: ConvMessage) => Promise<SkipOutcome>;
  resubmitEdited: (message: ConvMessage, text: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const state = useRef({ ready, failed });
  state.current = { ready: ready && !failed, failed };
  const actions = useRef({
    retry,
    alternative,
    skipClarification,
    resubmitEdited,
  });
  actions.current = { retry, alternative, skipClarification, resubmitEdited };

  const notReady = useCallback(() => {
    if (state.current.ready) return false;
    if (state.current.failed) {
      messageApi.error(t('v2.composer.loadFailedUseRetry'));
    } else {
      messageApi.info(t('v2.composer.rCalculating'));
    }
    return true;
  }, [messageApi, t]);

  return {
    retry: useCallback(
      (message: ConvMessage) =>
        notReady() ? Promise.resolve(null) : actions.current.retry(message),
      [notReady],
    ),
    alternative: useCallback(
      (message: ConvMessage) =>
        notReady()
          ? Promise.resolve(null)
          : actions.current.alternative(message),
      [notReady],
    ),
    skipClarification: useCallback(
      (message: ConvMessage) =>
        notReady()
          ? Promise.resolve({ sent: false, refusal: null })
          : actions.current.skipClarification(message),
      [notReady],
    ),
    resubmitEdited: useCallback(
      (message: ConvMessage, text: string) =>
        notReady()
          ? Promise.resolve(false)
          : actions.current.resubmitEdited(message, text),
      [notReady],
    ),
  };
};
